<!--
/**
 * AI:
 * Status: 'in progress'
 * Type: Component Template
 * SubType: ObjectBaseComponent
 * Reason: RpLogicReport detail page with overview, pipeline visualization, configuration, execution and output field tabs
 */
-->
@if (initialized && dto) {
    <!-- Page Heading Section -->
    <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div class="flex flex-column gap-2">
            <div class="flex align-items-center gap-3">
                <h1 class="text-3xl font-bold m-0">{{ dto.name }}</h1>
                <p-tag
                    [value]="getReportStatusLabel(dto.reportStatus)"
                    [severity]="getReportStatusSeverity(dto.reportStatus)"
                    [rounded]="true">
                </p-tag>
            </div>
            <div class="flex align-items-center gap-2">
                <span class="text-color-secondary">{{ dto.reportGroupDtoName || 'Keine Gruppe' }}</span>
                @if (dto.alias) {
                    <span class="text-color-secondary">|</span>
                    <code class="text-sm text-primary">{{ dto.alias }}</code>
                }
            </div>
        </div>
    </div>

    <!-- PrimeNG Tabs -->
    <p-tabs [(value)]="activeTabIndex" class="mb-5">
        <p-tablist>
            <p-tab [value]="0">
                <i class="fa-regular fa-grid-2 mr-2"></i>
                <span>Uebersicht</span>
            </p-tab>
            <p-tab [value]="1">
                <i class="fa-regular fa-sitemap mr-2"></i>
                <span>Pipeline</span>
            </p-tab>
            <p-tab [value]="2">
                <i class="fa-regular fa-gear mr-2"></i>
                <span>Konfiguration</span>
            </p-tab>
            <p-tab [value]="3">
                <i class="fa-regular fa-play mr-2"></i>
                <span>Ausfuehrung</span>
            </p-tab>
            <p-tab [value]="4">
                <i class="fa-regular fa-table mr-2"></i>
                <span>Ausgabefelder</span>
            </p-tab>
        </p-tablist>

        <p-tabpanels>

            <!-- ================================================================ -->
            <!-- Tab 0: Uebersicht -->
            <!-- ================================================================ -->
            <p-tabpanel [value]="0">
                @if (dto.description) {
                    <div class="p-3 surface-ground border-round border-1 surface-border mb-4">
                        <p class="m-0 text-color-secondary line-height-3">{{ dto.description }}</p>
                    </div>
                }

                <!-- KPI Cards -->
                <div class="grid mb-5">
                    <div class="col-12 sm:col-6 lg:col-3">
                        <p-card styleClass="h-full border-left-3 border-primary">
                            <div class="flex flex-column gap-2">
                                <span class="text-xs font-bold text-color-secondary uppercase">Status</span>
                                <div class="flex align-items-center justify-content-between">
                                    <p-tag
                                        [value]="getReportStatusLabel(dto.reportStatus)"
                                        [severity]="getReportStatusSeverity(dto.reportStatus)">
                                    </p-tag>
                                    <i class="fa-regular fa-circle-check text-3xl text-primary opacity-30"></i>
                                </div>
                            </div>
                        </p-card>
                    </div>
                    <div class="col-12 sm:col-6 lg:col-3">
                        <p-card styleClass="h-full">
                            <div class="flex flex-column gap-2">
                                <span class="text-xs font-bold text-color-secondary uppercase">Schritte</span>
                                <div class="flex align-items-center justify-content-between">
                                    <span class="text-2xl font-bold text-blue-500">{{ pipelineSteps?.length || 0 }}</span>
                                    <i class="fa-regular fa-sitemap text-3xl text-blue-300 opacity-30"></i>
                                </div>
                            </div>
                        </p-card>
                    </div>
                    <div class="col-12 sm:col-6 lg:col-3">
                        <p-card styleClass="h-full">
                            <div class="flex flex-column gap-2">
                                <span class="text-xs font-bold text-color-secondary uppercase">Parameter</span>
                                <div class="flex align-items-center justify-content-between">
                                    <span class="text-2xl font-bold text-orange-500">{{ selectionParamEntries?.length || 0 }}</span>
                                    <i class="fa-regular fa-sliders text-3xl text-orange-300 opacity-30"></i>
                                </div>
                            </div>
                        </p-card>
                    </div>
                    <div class="col-12 sm:col-6 lg:col-3">
                        <p-card styleClass="h-full">
                            <div class="flex flex-column gap-2">
                                <span class="text-xs font-bold text-color-secondary uppercase">Ausgabefelder</span>
                                <div class="flex align-items-center justify-content-between">
                                    <span class="text-2xl font-bold text-green-500">{{ outputFieldEntries?.length || 0 }}</span>
                                    <i class="fa-regular fa-table text-3xl text-green-300 opacity-30"></i>
                                </div>
                            </div>
                        </p-card>
                    </div>
                </div>

                <!-- Main Content Grid: Metadata + Actions -->
                <div class="grid">
                    <!-- Left Column: Metadata -->
                    <div class="col-12 lg:col-8">
                        <p-card>
                            <ng-template pTemplate="header">
                                <div class="flex align-items-center px-4 py-3 surface-ground border-bottom-1 surface-border">
                                    <h3 class="m-0 font-bold flex align-items-center gap-2">
                                        <i class="fa-regular fa-circle-info text-primary"></i>
                                        Details
                                    </h3>
                                </div>
                            </ng-template>

                            <div class="grid">
                                <div class="col-12 md:col-6 flex flex-column gap-1 mb-3">
                                    <span class="text-xs font-bold text-color-secondary uppercase">Alias</span>
                                    <span class="font-medium">{{ dto.alias || '-' }}</span>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-1 mb-3">
                                    <span class="text-xs font-bold text-color-secondary uppercase">Report-Gruppe</span>
                                    <span class="font-medium">{{ dto.reportGroupDtoName || '-' }}</span>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-1 mb-3">
                                    <span class="text-xs font-bold text-color-secondary uppercase">Validierung</span>
                                    <span class="font-medium">
                                        @if (isPublished()) {
                                            <i class="fa-regular fa-circle-check text-green-500 mr-1"></i>Validiert
                                        } @else {
                                            <i class="fa-regular fa-triangle-exclamation text-orange-500 mr-1"></i>Nicht validiert
                                        }
                                    </span>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-1 mb-3">
                                    <span class="text-xs font-bold text-color-secondary uppercase">Erstellt</span>
                                    <span class="font-medium">{{ dto.createdDate | date:'dd.MM.yyyy - HH:mm' }}</span>
                                </div>
                                <div class="col-12 md:col-6 flex flex-column gap-1 mb-3">
                                    <span class="text-xs font-bold text-color-secondary uppercase">Letzte Aenderung</span>
                                    <span class="font-medium">{{ dto.modifiedDate | date:'dd.MM.yyyy - HH:mm' }}</span>
                                </div>
                            </div>
                        </p-card>
                    </div>

                    <!-- Right Column: Action Panel -->
                    <div class="col-12 lg:col-4">
                        <p-card>
                            <ng-template pTemplate="header">
                                <div class="flex align-items-center px-4 py-3 surface-ground border-bottom-1 surface-border">
                                    <h3 class="m-0 font-bold flex align-items-center gap-2">
                                        <i class="fa-regular fa-bolt text-primary"></i>
                                        Aktionen
                                    </h3>
                                </div>
                            </ng-template>

                            <div class="flex flex-column gap-3">
                                <p-button
                                    label="Ausfuehren"
                                    icon="fa-regular fa-play"
                                    iconPos="left"
                                    styleClass="w-full justify-content-start"
                                    [disabled]="!isPublished()"
                                    (onClick)="activeTabIndex = 3">
                                </p-button>
                                <p-button
                                    label="Excel Export"
                                    icon="fa-regular fa-file-excel"
                                    iconPos="left"
                                    [outlined]="true"
                                    severity="success"
                                    styleClass="w-full justify-content-start"
                                    [disabled]="!isPublished()"
                                    (onClick)="exportExcel()">
                                </p-button>
                                <p-button
                                    label="CSV Export"
                                    icon="fa-regular fa-file-csv"
                                    iconPos="left"
                                    [outlined]="true"
                                    severity="info"
                                    styleClass="w-full justify-content-start"
                                    [disabled]="!isPublished()"
                                    (onClick)="exportCsv()">
                                </p-button>
                            </div>
                        </p-card>
                    </div>
                </div>
            </p-tabpanel>

            <!-- ================================================================ -->
            <!-- Tab 1: Pipeline Visualization -->
            <!-- ================================================================ -->
            <p-tabpanel [value]="1">
                @if (pipelineLoading) {
                    <div class="flex align-items-center justify-content-center p-6">
                        <p-progressSpinner strokeWidth="4" [style]="{width: '2.5rem', height: '2.5rem'}"></p-progressSpinner>
                        <span class="ml-3 text-color-secondary">Pipeline wird geladen...</span>
                    </div>
                } @else {
                    <!-- Pipeline Flow -->
                    <div class="pipeline-container flex align-items-start overflow-x-auto p-3">
                        <!-- Input Node: Selection Parameters -->
                        <div class="pipeline-node pipeline-node--input">
                            <div class="flex align-items-center gap-2 mb-2">
                                <i class="fa-regular fa-right-to-bracket text-green-600"></i>
                                <span class="font-bold text-sm">Eingabe</span>
                            </div>
                            @if (selectionParamEntries.length > 0) {
                                @for (param of selectionParamEntries; track param.id) {
                                    <div class="text-xs text-color-secondary mb-1">
                                        <i class="fa-regular fa-circle-dot text-green-400 mr-1"></i>{{ param.name }}
                                    </div>
                                }
                            } @else {
                                <div class="text-xs text-color-secondary">Keine Parameter</div>
                            }
                        </div>

                        <!-- Step Nodes with Connectors -->
                        @for (node of pipelineNodes; track node.step.id; let idx = $index) {
                            <!-- Connector -->
                            <div class="pipeline-connector">
                                <div class="pipeline-connector__line"></div>
                                <div class="pipeline-connector__arrow"></div>
                            </div>

                            <!-- Step Node -->
                            <div class="pipeline-node"
                                 [class.pipeline-node--ql]="node.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY"
                                 [class.pipeline-node--lg]="node.step.stepType === RpLogicReportStepTypeEnum.LG_LOGIC"
                                 [class.pipeline-node--selected]="node.isSelected"
                                 (click)="selectPipelineNode(node)">
                                <!-- Number Badge -->
                                <div class="pipeline-node__number"
                                     [class.pipeline-node__number--ql]="node.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY"
                                     [class.pipeline-node__number--lg]="node.step.stepType === RpLogicReportStepTypeEnum.LG_LOGIC">
                                    {{ idx + 1 }}
                                </div>

                                <!-- Type Icon -->
                                <div class="flex align-items-center gap-2 mb-2">
                                    <i [class]="getStepTypeIcon(node.step.stepType)"
                                       [class.text-blue-600]="node.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY"
                                       [class.text-orange-600]="node.step.stepType === RpLogicReportStepTypeEnum.LG_LOGIC">
                                    </i>
                                    <span class="font-bold text-sm white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ node.step.name }}</span>
                                </div>

                                <!-- Type Tag -->
                                <p-tag
                                    [value]="getStepTypeLabel(node.step.stepType)"
                                    [severity]="node.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY ? 'info' : 'warn'"
                                    class="mb-2">
                                </p-tag>

                                <!-- Result Alias -->
                                @if (node.step.resultAlias) {
                                    <div class="mt-2">
                                        <code class="text-xs surface-ground border-round px-2 py-1 border-1 surface-border">{{ node.step.resultAlias }}</code>
                                    </div>
                                }

                                <!-- Incoming Mappings -->
                                @if (node.incomingMappings.length > 0) {
                                    <div class="mt-2">
                                        @for (mapping of node.incomingMappings; track mapping.id) {
                                            <div class="text-xs text-color-secondary mt-1">
                                                <i class="fa-regular fa-arrow-right-to-bracket mr-1"></i>{{ mapping.sourceResultAlias }}
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        }

                        <!-- Connector to Output -->
                        @if (pipelineNodes.length > 0) {
                            <div class="pipeline-connector">
                                <div class="pipeline-connector__line"></div>
                                <div class="pipeline-connector__arrow"></div>
                            </div>
                        }

                        <!-- Output Node: Output Fields -->
                        <div class="pipeline-node pipeline-node--output">
                            <div class="flex align-items-center gap-2 mb-2">
                                <i class="fa-regular fa-right-from-bracket text-purple-600"></i>
                                <span class="font-bold text-sm">Ausgabe</span>
                            </div>
                            @if (outputFieldEntries.length > 0) {
                                @for (field of outputFieldEntries; track field.id) {
                                    <div class="text-xs text-color-secondary mb-1">
                                        <i class="fa-regular fa-circle-dot text-purple-400 mr-1"></i>{{ field.label }}
                                    </div>
                                }
                            } @else {
                                <div class="text-xs text-color-secondary">Keine Felder definiert</div>
                            }
                        </div>
                    </div>

                    @if (pipelineNodes.length === 0 && !pipelineLoading) {
                        <div class="flex align-items-center justify-content-center p-4">
                            <p-message severity="info" text="Keine Pipeline-Schritte konfiguriert. Erstellen Sie Schritte im Tab 'Konfiguration'."></p-message>
                        </div>
                    }

                    <!-- Detail Panel (slides down below pipeline when a node is selected) -->
                    @if (selectedNode) {
                        <div class="pipeline-detail-panel px-3 pb-3">
                            <p-card>
                                <ng-template pTemplate="header">
                                    <div class="flex align-items-center justify-content-between px-4 py-3 surface-ground border-bottom-1 surface-border">
                                        <h3 class="m-0 font-bold flex align-items-center gap-2">
                                            <i [class]="getStepTypeIcon(selectedNode.step.stepType) + ' text-primary'"></i>
                                            {{ selectedNode.step.name }}
                                        </h3>
                                        <p-button
                                            icon="fa-regular fa-xmark"
                                            [rounded]="true"
                                            [text]="true"
                                            severity="secondary"
                                            (onClick)="deselectPipelineNode()">
                                        </p-button>
                                    </div>
                                </ng-template>

                                <div class="grid">
                                    <!-- Left: Step Properties -->
                                    <div class="col-12 md:col-6">
                                        <h4 class="mt-0 mb-3 font-bold">Schritt-Eigenschaften</h4>
                                        <div class="flex flex-column gap-3">
                                            <div class="flex flex-column gap-1">
                                                <span class="text-xs font-bold text-color-secondary uppercase">Typ</span>
                                                <p-tag
                                                    [value]="getStepTypeLabel(selectedNode.step.stepType)"
                                                    [severity]="selectedNode.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY ? 'info' : 'warn'">
                                                </p-tag>
                                            </div>
                                            <div class="flex flex-column gap-1">
                                                <span class="text-xs font-bold text-color-secondary uppercase">Reihenfolge</span>
                                                <span class="font-medium">{{ selectedNode.step.orderPriority }}</span>
                                            </div>
                                            <div class="flex flex-column gap-1">
                                                <span class="text-xs font-bold text-color-secondary uppercase">Ergebnis-Alias</span>
                                                <code class="font-medium">{{ selectedNode.step.resultAlias || '-' }}</code>
                                            </div>
                                            @if (selectedNode.step.description) {
                                                <div class="flex flex-column gap-1">
                                                    <span class="text-xs font-bold text-color-secondary uppercase">Beschreibung</span>
                                                    <span class="text-color-secondary">{{ selectedNode.step.description }}</span>
                                                </div>
                                            }
                                            @if (selectedNode.step.logicDtoName) {
                                                <div class="flex flex-column gap-1">
                                                    <span class="text-xs font-bold text-color-secondary uppercase">Logic</span>
                                                    <span class="font-medium">{{ selectedNode.step.logicDtoName }}</span>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <!-- Right: Incoming Mappings -->
                                    <div class="col-12 md:col-6">
                                        <h4 class="mt-0 mb-3 font-bold">Eingehende Zuordnungen</h4>
                                        @if (selectedNode.incomingMappings.length > 0) {
                                            <p-table [value]="selectedNode.incomingMappings" styleClass="p-datatable-sm p-datatable-striped">
                                                <ng-template pTemplate="header">
                                                    <tr>
                                                        <th>Quelle</th>
                                                        <th>Zielvariable</th>
                                                    </tr>
                                                </ng-template>
                                                <ng-template pTemplate="body" let-mapping>
                                                    <tr>
                                                        <td><code>{{ mapping.sourceResultAlias }}</code></td>
                                                        <td><code>{{ mapping.targetVariableName }}</code></td>
                                                    </tr>
                                                </ng-template>
                                            </p-table>
                                        } @else {
                                            <p class="text-color-secondary text-sm">Keine Zuordnungen vorhanden.</p>
                                        }
                                    </div>
                                </div>

                                <!-- QL JSON (only for QL_QUERY steps) -->
                                @if (selectedNode.step.stepType === RpLogicReportStepTypeEnum.QL_QUERY && selectedNode.step.qlRequestJson) {
                                    <p-divider></p-divider>
                                    <h4 class="mt-0 mb-2 font-bold">QL Request JSON</h4>
                                    <pre class="ql-json-pre m-0">{{ selectedNode.step.qlRequestJson }}</pre>
                                }
                            </p-card>
                        </div>
                    }
                }
            </p-tabpanel>

            <!-- ================================================================ -->
            <!-- Tab 2: Konfiguration -->
            <!-- ================================================================ -->
            <p-tabpanel [value]="2">
                <div class="p-3">
                    <!-- Steps Widget -->
                    <div class="mb-4">
                        @if (stepsWidget) {
                            <mvs-widget
                                [widgetData]="stepsWidget"
                                (onObjectSelect)="handleStepSelect($event)">
                            </mvs-widget>
                        }
                    </div>

                    <!-- Mappings Widget (shown after step selection) -->
                    @if (mappingsWidget) {
                        <div class="mb-4">
                            <mvs-widget [widgetData]="mappingsWidget"></mvs-widget>
                        </div>
                    }

                    <!-- Selection Params Widget -->
                    <div class="mb-4">
                        @if (selectionParamsWidget) {
                            <mvs-widget [widgetData]="selectionParamsWidget"></mvs-widget>
                        }
                    </div>

                    <!-- Output Fields Widget -->
                    <div class="mb-4">
                        @if (outputFieldsWidget) {
                            <mvs-widget [widgetData]="outputFieldsWidget"></mvs-widget>
                        }
                    </div>
                </div>
            </p-tabpanel>

            <!-- ================================================================ -->
            <!-- Tab 3: Ausfuehrung -->
            <!-- ================================================================ -->
            <p-tabpanel [value]="3">
                <div class="p-3">

                    <!-- Loading state for selection form -->
                    @if (selectionFormLoading) {
                        <div class="flex align-items-center gap-2 mb-4">
                            <i class="fa-regular fa-spinner fa-spin"></i>
                            <span class="text-500">Selektionsformular wird geladen...</span>
                        </div>
                    }

                    <!-- Selection Form -->
                    @if (selectionFormLoaded) {
                        @if (hasSelectionFields()) {
                            <div class="surface-card p-4 border-round border-1 surface-border mb-4">
                                <div class="flex align-items-center gap-2 mb-3">
                                    <i class="fa-regular fa-sliders text-primary"></i>
                                    <span class="text-lg font-semibold">Selektionsparameter</span>
                                </div>

                                <div class="grid" [formGroup]="selectionFormGroup">
                                    @for (field of selectionForm.fields; track field.variableName) {
                                        <div class="col-12 md:col-6 mb-3">
                                            <label class="block text-sm font-medium mb-1" [attr.for]="field.variableName">
                                                {{ field.label }}
                                                @if (field.mandatory) {
                                                    <span class="text-red-500 ml-1">*</span>
                                                }
                                            </label>

                                            @switch (field.uiInputControl) {
                                                @case ('date') {
                                                    <p-datePicker
                                                        [formControlName]="field.variableName"
                                                        [inputId]="field.variableName"
                                                        dateFormat="dd.mm.yy"
                                                        [showIcon]="true"
                                                        styleClass="w-full">
                                                    </p-datePicker>
                                                }
                                                @case ('dateTime') {
                                                    <p-datePicker
                                                        [formControlName]="field.variableName"
                                                        [inputId]="field.variableName"
                                                        dateFormat="dd.mm.yy"
                                                        [showTime]="true"
                                                        [showIcon]="true"
                                                        styleClass="w-full">
                                                    </p-datePicker>
                                                }
                                                @case ('numberInt') {
                                                    <p-inputNumber
                                                        [formControlName]="field.variableName"
                                                        [inputId]="field.variableName"
                                                        [useGrouping]="false"
                                                        styleClass="w-full">
                                                    </p-inputNumber>
                                                }
                                                @case ('numberDecimal') {
                                                    <p-inputNumber
                                                        [formControlName]="field.variableName"
                                                        [inputId]="field.variableName"
                                                        [minFractionDigits]="2"
                                                        [maxFractionDigits]="4"
                                                        [useGrouping]="false"
                                                        styleClass="w-full">
                                                    </p-inputNumber>
                                                }
                                                @case ('checkbox') {
                                                    <div class="mt-1">
                                                        <p-checkbox
                                                            [formControlName]="field.variableName"
                                                            [inputId]="field.variableName"
                                                            [binary]="true">
                                                        </p-checkbox>
                                                    </div>
                                                }
                                                @case ('select') {
                                                    <p-select
                                                        [formControlName]="field.variableName"
                                                        [inputId]="field.variableName"
                                                        [options]="field.valueList"
                                                        optionLabel="label"
                                                        optionValue="key"
                                                        placeholder="Bitte waehlen..."
                                                        styleClass="w-full">
                                                    </p-select>
                                                }
                                                @case ('textarea') {
                                                    <textarea
                                                        pTextarea
                                                        [formControlName]="field.variableName"
                                                        [id]="field.variableName"
                                                        rows="3"
                                                        class="w-full">
                                                    </textarea>
                                                }
                                                @default {
                                                    <input
                                                        pInputText
                                                        [formControlName]="field.variableName"
                                                        [id]="field.variableName"
                                                        class="w-full" />
                                                }
                                            }

                                            @if (field.description) {
                                                <small class="block text-500 mt-1">{{ field.description }}</small>
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        <!-- Execute + Export Buttons -->
                        <div class="flex align-items-center gap-3 mb-4">
                            <p-button
                                label="Ausfuehren"
                                icon="fa-regular fa-play"
                                [loading]="executionBusy"
                                [disabled]="executionBusy || !isPublished() || (hasSelectionFields() && selectionFormGroup?.invalid)"
                                (onClick)="executeReport()">
                            </p-button>

                            @if (exportResult || executionResult) {
                                <p-button
                                    label="Excel"
                                    icon="fa-regular fa-file-excel"
                                    [outlined]="true"
                                    severity="success"
                                    [disabled]="executionBusy || !isPublished()"
                                    (onClick)="exportExcel()">
                                </p-button>
                                <p-button
                                    label="CSV"
                                    icon="fa-regular fa-file-csv"
                                    [outlined]="true"
                                    severity="info"
                                    [disabled]="executionBusy || !isPublished()"
                                    (onClick)="exportCsv()">
                                </p-button>
                            }

                            @if (!isPublished()) {
                                <span class="text-orange-500 text-sm">
                                    <i class="fa-regular fa-triangle-exclamation mr-1"></i>
                                    Report muss den Status PUBLISHED haben
                                </span>
                            }
                        </div>
                    }

                    <!-- Structured Export Result (with columns) -->
                    @if (exportResult) {
                        <!-- Summary Cards -->
                        <div class="grid mb-4">
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Gesamtdauer</div>
                                    <div class="text-2xl font-bold">{{ exportResult.executionTimeMs }}ms</div>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Spalten</div>
                                    <div class="text-2xl font-bold">{{ exportResult.columns?.length || 0 }}</div>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Ergebnis-Zeilen</div>
                                    <div class="text-2xl font-bold">{{ exportResult.totalRows || exportResult.data?.length || 0 }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Result Data Table with typed columns -->
                        @if (exportResult.columns?.length > 0) {
                            <div class="mb-4">
                                <h4 class="mt-0 mb-2">Ergebnis-Daten</h4>
                                <p-table [value]="exportResult.data"
                                         styleClass="p-datatable-sm p-datatable-striped p-datatable-gridlines"
                                         [scrollable]="true" scrollHeight="25rem"
                                         [paginator]="exportResult.data?.length > 20" [rows]="20">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            @for (col of exportResult.columns; track col.fieldKey) {
                                                <th>{{ col.label }}</th>
                                            }
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-row>
                                        <tr>
                                            @for (col of exportResult.columns; track col.fieldKey) {
                                                <td>{{ row[col.fieldKey] }}</td>
                                            }
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage">
                                        <tr>
                                            <td [attr.colspan]="exportResult.columns?.length" class="text-center p-4 text-500">
                                                Keine Ergebnis-Daten vorhanden.
                                            </td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            </div>
                        }
                    }

                    <!-- Legacy Execution Result (without selection params) -->
                    @if (executionResult) {
                        <!-- Summary Cards -->
                        <div class="grid mb-4">
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Gesamtdauer</div>
                                    <div class="text-2xl font-bold">{{ executionResult.executionTimeMs }}ms</div>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Schritte</div>
                                    <div class="text-2xl font-bold">{{ executionResult.stepExecutions?.length || 0 }}</div>
                                </div>
                            </div>
                            <div class="col-4">
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    <div class="text-500 text-sm mb-1">Ergebnis-Zeilen</div>
                                    <div class="text-2xl font-bold">{{ executionResult.finalResult?.length || 0 }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Execution Timeline -->
                        @if (executionResult.stepExecutions?.length > 0) {
                            <div class="mb-4">
                                <h4 class="mt-0 mb-2">Ausfuehrungs-Timeline</h4>
                                <div class="surface-card p-3 border-round border-1 surface-border">
                                    @for (stepExec of executionResult.stepExecutions; track stepExec.resultAlias) {
                                        <div class="flex align-items-center gap-3 mb-2">
                                            <span class="text-sm font-medium w-8rem white-space-nowrap overflow-hidden text-overflow-ellipsis">{{ stepExec.stepName }}</span>
                                            <div class="flex-grow-1">
                                                <div class="execution-timeline__bar"
                                                     [style.width.%]="(stepExec.executionTimeMs / getMaxExecutionTime()) * 100"
                                                     [class.bg-blue-400]="stepExec.stepType === 'QL_QUERY'"
                                                     [class.bg-orange-400]="stepExec.stepType !== 'QL_QUERY'">
                                                </div>
                                            </div>
                                            <span class="text-xs text-color-secondary w-4rem text-right">{{ stepExec.executionTimeMs }}ms</span>
                                            <p-tag
                                                [value]="stepExec.success ? 'OK' : 'Fehler'"
                                                [severity]="stepExec.success ? 'success' : 'danger'"
                                                class="w-4rem">
                                            </p-tag>
                                        </div>
                                    }
                                </div>
                            </div>
                        }

                        <!-- Step Executions Table -->
                        <div class="mb-4">
                            <h4 class="mt-0 mb-2">Schritt-Ausfuehrungen</h4>
                            <p-table [value]="executionResult.stepExecutions" styleClass="p-datatable-sm p-datatable-striped">
                                <ng-template pTemplate="header">
                                    <tr>
                                        <th>Schritt</th>
                                        <th>Alias</th>
                                        <th>Typ</th>
                                        <th>Dauer (ms)</th>
                                        <th>Status</th>
                                    </tr>
                                </ng-template>
                                <ng-template pTemplate="body" let-step>
                                    <tr>
                                        <td>{{ step.stepName }}</td>
                                        <td><code>{{ step.resultAlias }}</code></td>
                                        <td>
                                            <p-tag [value]="step.stepType" [severity]="step.stepType === 'QL_QUERY' ? 'info' : 'warn'"></p-tag>
                                        </td>
                                        <td class="text-right">{{ step.executionTimeMs }}</td>
                                        <td>
                                            <p-tag [value]="step.success ? 'Erfolg' : 'Fehler'" [severity]="step.success ? 'success' : 'danger'"></p-tag>
                                        </td>
                                    </tr>
                                </ng-template>
                            </p-table>
                        </div>

                        <!-- Final Result Data Table (dynamic columns) -->
                        @if (finalResultColumns.length > 0) {
                            <div class="mb-4">
                                <h4 class="mt-0 mb-2">Ergebnis-Daten</h4>
                                <p-table [value]="executionResult.finalResult"
                                         styleClass="p-datatable-sm p-datatable-striped p-datatable-gridlines"
                                         [scrollable]="true" scrollHeight="25rem"
                                         [paginator]="executionResult.finalResult?.length > 20" [rows]="20">
                                    <ng-template pTemplate="header">
                                        <tr>
                                            @for (col of finalResultColumns; track col) {
                                                <th>{{ col }}</th>
                                            }
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="body" let-row>
                                        <tr>
                                            @for (col of finalResultColumns; track col) {
                                                <td>{{ row[col] }}</td>
                                            }
                                        </tr>
                                    </ng-template>
                                    <ng-template pTemplate="emptymessage">
                                        <tr>
                                            <td [attr.colspan]="finalResultColumns.length" class="text-center p-4 text-500">
                                                Keine Ergebnis-Daten vorhanden. Pruefen Sie die Pipeline-Schritte und fuehren Sie erneut aus.
                                            </td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            </div>
                        }

                        <!-- Pipeline Context -->
                        @if (pipelineContextAliases.length > 0) {
                            <div>
                                <h4 class="mt-0 mb-2">Pipeline Context</h4>
                                @for (alias of pipelineContextAliases; track alias) {
                                    <div class="mb-2">
                                        <div class="surface-card p-3 border-round border-1 surface-border">
                                            <div class="font-bold mb-2">{{ alias }}</div>
                                            <pre class="text-sm m-0 overflow-auto rp-context-pre">{{ executionResult.pipelineContext[alias] | json }}</pre>
                                        </div>
                                    </div>
                                }
                            </div>
                        }
                    }

                    <!-- Error State -->
                    @if (executionError) {
                        <div class="p-3 border-round bg-red-50 text-red-700">
                            <i class="fa-regular fa-circle-xmark mr-2"></i>{{ executionError }}
                        </div>
                    }
                </div>
            </p-tabpanel>

            <!-- ================================================================ -->
            <!-- Tab 4: Ausgabefelder -->
            <!-- ================================================================ -->
            <p-tabpanel [value]="4">
                <div class="p-3">
                    @if (outputFieldsWidget) {
                        <mvs-widget [widgetData]="outputFieldsWidget"></mvs-widget>
                    }
                </div>
            </p-tabpanel>

        </p-tabpanels>
    </p-tabs>
} @else {
    <div class="flex align-items-center justify-content-center p-8">
        <p-progressSpinner strokeWidth="4" [style]="{width: '3rem', height: '3rem'}"></p-progressSpinner>
    </div>
}
